export type AppState = {
    enteredInformation: boolean,
    firstName: string,
    lastName: string,
    showChat: boolean,
    lastMessage: string,
    hasOpened: boolean,
    hasShownPrompt: boolean,
    lastLocation: string,
    shrinkWidget: boolean,
    isLandscape: boolean
}

export const initialState: AppState = {
    enteredInformation: false,
    firstName: "",
    lastName: "",
    showChat: false,
    hasOpened: false,
    hasShownPrompt: false,
    lastMessage: "",
    lastLocation: "",
    shrinkWidget: false,
    isLandscape: true
}

export type AppAction =
    | { type: 'setEnteredInformation', hasEnteredInformation: boolean }
    | { type: 'setShowChat', showChat: boolean }
    | { type: 'setShrinkWidget', shrinkWidget: boolean }
    | { type: 'setHasOpened', hasOpened: boolean }
    | { type: 'setLastLocation', location: string }
    | { type: 'setIsLandscape', isLandscape: boolean }
    | { type: 'setAppState', appState: AppState }

export const storageIdentifier = "edullaStartChat"

const getStorageIdentifier = () => {
    const tid = sessionStorage.getItem("edullaTid") ?? "";
    return `${storageIdentifier}${tid}`;
}

const normalizeAppState = (state?: Partial<AppState> | null): AppState => {
    const merged = { ...initialState, ...(state ?? {}) } as Partial<AppState>;

    const coerceString = (value: unknown, fallback = "") => typeof value === "string" ? value : fallback;
    const coerceBoolean = (value: unknown, fallback = false) => typeof value === "boolean" ? value : fallback;

    let lastLocation = coerceString(merged.lastLocation, initialState.lastLocation).trim();
    if (!lastLocation || lastLocation === "undefined" || lastLocation === "/index.html") {
        lastLocation = initialState.lastLocation;
    } else if (!lastLocation.startsWith("/")) {
        lastLocation = `/${lastLocation}`;
    }

    return {
        enteredInformation: coerceBoolean(merged.enteredInformation),
        firstName: coerceString(merged.firstName),
        lastName: coerceString(merged.lastName),
        showChat: coerceBoolean(merged.showChat),
        lastMessage: coerceString(merged.lastMessage),
        hasOpened: coerceBoolean(merged.hasOpened),
        hasShownPrompt: coerceBoolean(merged.hasShownPrompt),
        lastLocation,
        shrinkWidget: coerceBoolean(merged.shrinkWidget),
        isLandscape: coerceBoolean(merged.isLandscape, initialState.isLandscape)
    };
}

const persistState = (state: AppState): AppState => {
    const normalized = normalizeAppState(state);
    localStorage.setItem(getStorageIdentifier(), JSON.stringify(normalized));
    return normalized;
}

const getStoredState = (): AppState => {
    const storedValue = localStorage.getItem(getStorageIdentifier());
    if (!storedValue) {
        return initialState;
    }

    try {
        const parsed = JSON.parse(storedValue);
        return normalizeAppState(parsed);
    } catch (err) {
        return initialState;
    }
}

export const getCachedAppState = (): AppState => getStoredState();

export const startReducer = (state: AppState, action: AppAction) => {
    const currentState = getStoredState();

    switch (action.type) {
        case "setEnteredInformation":
            return persistState({ ...currentState, enteredInformation: action.hasEnteredInformation });
        case "setShowChat":
            return persistState({ ...currentState, showChat: action.showChat });
        case "setHasOpened":
            return persistState({ ...currentState, hasOpened: action.hasOpened });
        case "setLastLocation":
            return persistState({ ...currentState, lastLocation: action.location });
        case "setShrinkWidget":
            return persistState({ ...currentState, shrinkWidget: action.shrinkWidget });
        case "setIsLandscape":
            return persistState({ ...currentState, isLandscape: action.isLandscape });
        case "setAppState":
            return persistState(action.appState);
        default:
            return currentState;
    }
};

